import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, GripVertical, Save, MessageSquare, Copy } from 'lucide-react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InterviewBuilderPage = () => {
  const { projectId, interviewId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(!!interviewId);
  const [saving, setSaving] = useState(false);
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [targetInterviewees, setTargetInterviewees] = useState('');
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    if (interviewId) {
      const fetchInterview = async () => {
        try {
          const res = await axios.get(`${API}/interviews/${interviewId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setTitle(res.data.title);
          setDescription(res.data.description || '');
          setTargetInterviewees(res.data.target_interviewees?.toString() || '');
          setQuestions(res.data.questions || []);
        } catch (err) {
          toast.error('Failed to load interview schedule');
          navigate(`/project/${projectId}/data-collection`);
        } finally {
          setLoading(false);
        }
      };
      fetchInterview();
    }
  }, [interviewId, projectId, token, navigate]);

  const addQuestion = () => {
    const newQuestion = {
      id: uuidv4(),
      question: '',
      order: questions.length + 1
    };
    setQuestions([...questions, newQuestion]);
  };

  const updateQuestion = (index, value) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], question: value };
    setQuestions(updated);
  };

  const deleteQuestion = (index) => {
    const updated = questions.filter((_, i) => i !== index);
    // Update order numbers
    updated.forEach((q, i) => { q.order = i + 1; });
    setQuestions(updated);
  };

  const duplicateQuestion = (index) => {
    const original = questions[index];
    const duplicate = { ...original, id: uuidv4(), order: questions.length + 1 };
    setQuestions([...questions, duplicate]);
  };

  const moveQuestion = (fromIndex, toIndex) => {
    const updated = [...questions];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    // Update order numbers
    updated.forEach((q, i) => { q.order = i + 1; });
    setQuestions(updated);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (questions.length === 0) {
      toast.error('Please add at least one question');
      return;
    }
    if (questions.some(q => !q.question.trim())) {
      toast.error('Please fill in all questions');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        project_id: projectId,
        title: title.trim(),
        description: description.trim(),
        questions,
        target_interviewees: targetInterviewees ? parseInt(targetInterviewees) : null
      };

      if (interviewId) {
        await axios.put(`${API}/interviews/${interviewId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/interviews`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      toast.success('Interview schedule saved');
      navigate(`/project/${projectId}/data-collection`);
    } catch (err) {
      toast.error('Failed to save interview schedule');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(`/project/${projectId}/data-collection`)} 
                className="text-slate-500 hover:text-slate-700"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-emerald-600" />
                <h1 className="text-lg font-semibold text-slate-900">
                  {interviewId ? 'Edit Interview Schedule' : 'Create Interview Schedule'}
                </h1>
              </div>
            </div>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Schedule'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Interview Details */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Interview Details</h2>
          <div className="space-y-4">
            <div>
              <Label>Interview Title *</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., User Experience Interview"
                className="mt-1"
                data-testid="interview-title-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of the interview purpose and target participants..."
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                rows={2}
              />
            </div>
            <div>
              <Label>Target Number of Interviewees (optional)</Label>
              <Input
                type="number"
                value={targetInterviewees}
                onChange={(e) => setTargetInterviewees(e.target.value)}
                placeholder="e.g., 15"
                className="mt-1 max-w-xs"
                min="1"
              />
              <p className="text-xs text-slate-500 mt-1">Set a target to track your data collection progress</p>
            </div>
          </div>
        </div>

        {/* Questions */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Interview Questions ({questions.length})</h2>
          </div>

          <div className="space-y-4">
            {questions.map((question, index) => (
              <div 
                key={question.id} 
                className="bg-white rounded-xl border border-slate-200 p-4"
                data-testid={`question-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center gap-1">
                    <button 
                      className="cursor-move text-slate-400 hover:text-slate-600"
                      title="Drag to reorder"
                    >
                      <GripVertical className="w-5 h-5" />
                    </button>
                    {index > 0 && (
                      <button 
                        onClick={() => moveQuestion(index, index - 1)}
                        className="text-slate-400 hover:text-slate-600 text-xs"
                      >
                        ↑
                      </button>
                    )}
                    {index < questions.length - 1 && (
                      <button 
                        onClick={() => moveQuestion(index, index + 1)}
                        className="text-slate-400 hover:text-slate-600 text-xs"
                      >
                        ↓
                      </button>
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-emerald-600">Q{question.order}</span>
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded">Open-ended</span>
                    </div>
                    <textarea
                      value={question.question}
                      onChange={(e) => updateQuestion(index, e.target.value)}
                      placeholder="Enter your interview question..."
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      rows={2}
                      data-testid={`question-text-${index}`}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Tip: Ask open-ended questions that encourage detailed responses
                    </p>
                  </div>

                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => duplicateQuestion(index)}
                      className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                      title="Duplicate"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteQuestion(index)}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Add Question Button */}
          <button 
            onClick={addQuestion}
            className="w-full mt-4 p-4 border-2 border-dashed border-slate-300 rounded-xl hover:border-emerald-400 hover:bg-emerald-50 transition-colors flex items-center justify-center gap-2 text-slate-600 hover:text-emerald-600"
            data-testid="add-question-btn"
          >
            <Plus className="w-5 h-5" />
            Add Question
          </button>
        </div>

        {/* Sample Questions */}
        <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-6">
          <h3 className="font-medium text-emerald-900 mb-3">Sample Interview Questions</h3>
          <ul className="space-y-2 text-sm text-emerald-800">
            <li>• Can you describe your experience with [topic]?</li>
            <li>• What challenges have you faced when [doing something]?</li>
            <li>• How do you feel about [aspect of the topic]?</li>
            <li>• Can you walk me through the process of [action]?</li>
            <li>• What suggestions would you have for improving [thing]?</li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default InterviewBuilderPage;
